import { PubSubService } from '../_shared/pubSubServiceInterface';

export interface LayoutPreset {
  id: string;
  name: string;
  icon: string;
  layout: {
    numRows: number;
    numCols: number;
  };
  protocolId?: string;
  isAdvanced?: boolean;
}

export interface ProfessionalPresets {
  professionalType: string;
  defaultPresets: string[]; // IDs de los presets
  customPresets?: LayoutPreset[];
}

const EVENTS = {
  PRESETS_CHANGED: 'event::layoutpresets:changed',
  PRESET_SELECTED: 'event::layoutpresets:selected',
};

class LayoutPresetsService extends PubSubService {
  public static REGISTRATION = {
    name: 'layoutPresetsService',
    altName: 'LayoutPresetsService',
    create: ({ configuration = {} }) => {
      return new LayoutPresetsService();
    },
  };

  private _presets: Map<string, LayoutPreset> = new Map();
  private _professionalPresets: Map<string, ProfessionalPresets> = new Map();
  private _currentProfessional: string | null = null;
  public EVENTS = EVENTS;

  constructor() {
    super(EVENTS);
    this._initializeDefaultPresets();
    this._initializeProfessionalDefaults();
  }

  private _initializeDefaultPresets() {
    // Presets básicos comunes
    const commonPresets: LayoutPreset[] = [
      {
        id: '1x1',
        name: '1x1',
        icon: 'layout-common-1x1',
        layout: { numRows: 1, numCols: 1 },
      },
      {
        id: '1x2',
        name: '1x2',
        icon: 'layout-common-1x2',
        layout: { numRows: 1, numCols: 2 },
      },
      {
        id: '2x1',
        name: '2x1',
        icon: 'layout-common-2x1',
        layout: { numRows: 2, numCols: 1 },
      },
      {
        id: '2x2',
        name: '2x2',
        icon: 'layout-common-2x2',
        layout: { numRows: 2, numCols: 2 },
      },
      {
        id: '1x4',
        name: '1x4',
        icon: 'layout-common-1x4',
        layout: { numRows: 1, numCols: 4 },
      },
      {
        id: '2x3',
        name: '2x3',
        icon: 'layout-common-2x3',
        layout: { numRows: 2, numCols: 3 },
      },
      {
        id: '3x3',
        name: '3x3',
        icon: 'layout-common-3x3',
        layout: { numRows: 3, numCols: 3 },
      },
      {
        id: '4x1',
        name: '4x1',
        icon: 'layout-common-4x1',
        layout: { numRows: 4, numCols: 1 },
      },
      {
        id: '1x4',
        name: '1x4',
        icon: 'layout-common-1x4',
        layout: { numRows: 1, numCols: 4 },
      },
      {
        id: '2x4',
        name: '2x4',
        icon: 'layout-common-2x4',
        layout: { numRows: 2, numCols: 4 },
      },
    ];

    // Presets avanzados (con protocolos específicos)
    const advancedPresets: LayoutPreset[] = [
      {
        id: 'mpr',
        name: 'MPR',
        icon: 'layout-advanced-mpr',
        layout: { numRows: 1, numCols: 3 },
        protocolId: 'mpr',
        isAdvanced: true,
      },
      {
        id: 'axial-primary',
        name: 'Axial Primario',
        icon: 'layout-advanced-axial-primary',
        layout: { numRows: 1, numCols: 2 },
        protocolId: 'primaryAxial',
        isAdvanced: true,
      },
      {
        id: '3d-primary',
        name: '3D Primario',
        icon: 'layout-advanced-3d-primary',
        layout: { numRows: 1, numCols: 2 },
        protocolId: 'primary3D',
        isAdvanced: true,
      },
      {
        id: '3d-only',
        name: 'Solo 3D',
        icon: 'layout-advanced-3d-only',
        layout: { numRows: 1, numCols: 1 },
        protocolId: 'only3D',
        isAdvanced: true,
      },
      {
        id: '3d-four-up',
        name: '3D Cuatro Vistas',
        icon: 'layout-advanced-3d-four-up',
        layout: { numRows: 2, numCols: 2 },
        protocolId: 'fourUp',
        isAdvanced: true,
      },
    ];

    [...commonPresets, ...advancedPresets].forEach(preset => {
      this._presets.set(preset.id, preset);
    });
  }

  private _initializeProfessionalDefaults() {
    // Configuraciones por defecto para diferentes tipos de profesionales
    const professionalDefaults: ProfessionalPresets[] = [
      {
        professionalType: 'radiologist',
        defaultPresets: ['1x4', '2x2', 'mpr'],
      },
      {
        professionalType: 'cardiologist',
        defaultPresets: ['1x4', '2x2', '2x3'],
      },
      {
        professionalType: 'orthopedic',
        defaultPresets: ['1x4', '1x2', 'mpr'],
      },
      {
        professionalType: 'neurologist',
        defaultPresets: ['1x4', 'mpr', '2x2'],
      },
      {
        professionalType: 'traumatologist',
        defaultPresets: ['1x4', '2x2', '1x2'],
      },
      {
        professionalType: 'general',
        defaultPresets: ['1x4', '1x2', '2x2'],
      },
    ];

    professionalDefaults.forEach(config => {
      this._professionalPresets.set(config.professionalType, config);
    });
  }

  /**
   * Establece el tipo de profesional actual
   */
  public setProfessionalType(professionalType: string | null) {
    this._currentProfessional = professionalType;
    this._broadcastEvent(EVENTS.PRESETS_CHANGED, {
      professionalType,
      presets: this.getCurrentPresets(),
    });
  }

  /**
   * Obtiene el tipo de profesional actual
   */
  public getCurrentProfessional(): string | null {
    return this._currentProfessional;
  }

  /**
   * Obtiene los presets para el profesional actual
   */
  public getCurrentPresets(): LayoutPreset[] {
    if (!this._currentProfessional) {
      return [];
    }

    const professionalConfig = this._professionalPresets.get(this._currentProfessional);
    if (!professionalConfig) {
      return [];
    }

    const presets: LayoutPreset[] = [];
    
    // Agregar presets por defecto
    professionalConfig.defaultPresets.forEach(presetId => {
      const preset = this._presets.get(presetId);
      if (preset) {
        presets.push(preset);
      }
    });

    // Agregar presets personalizados si existen
    if (professionalConfig.customPresets) {
      presets.push(...professionalConfig.customPresets);
    }

    return presets;
  }

  /**
   * Obtiene todos los presets disponibles
   */
  public getAllPresets(): LayoutPreset[] {
    return Array.from(this._presets.values());
  }

  /**
   * Obtiene los presets comunes (no avanzados)
   */
  public getCommonPresets(): LayoutPreset[] {
    return Array.from(this._presets.values()).filter(preset => !preset.isAdvanced);
  }

  /**
   * Obtiene los presets avanzados
   */
  public getAdvancedPresets(): LayoutPreset[] {
    return Array.from(this._presets.values()).filter(preset => preset.isAdvanced);
  }

  /**
   * Agrega un preset personalizado para el profesional actual
   */
  public addCustomPreset(preset: LayoutPreset) {
    if (!this._currentProfessional) {
      throw new Error('No hay un profesional establecido');
    }

    const professionalConfig = this._professionalPresets.get(this._currentProfessional);
    if (!professionalConfig) {
      return;
    }

    if (!professionalConfig.customPresets) {
      professionalConfig.customPresets = [];
    }

    // Asignar un ID único si no tiene
    if (!preset.id) {
      preset.id = `custom_${Date.now()}`;
    }

    professionalConfig.customPresets.push(preset);
    this._savePresets();
    
    this._broadcastEvent(EVENTS.PRESETS_CHANGED, {
      professionalType: this._currentProfessional,
      presets: this.getCurrentPresets(),
    });
  }

  /**
   * Elimina un preset personalizado
   */
  public removeCustomPreset(presetId: string) {
    if (!this._currentProfessional) {
      return;
    }

    const professionalConfig = this._professionalPresets.get(this._currentProfessional);
    if (!professionalConfig || !professionalConfig.customPresets) {
      return;
    }

    professionalConfig.customPresets = professionalConfig.customPresets.filter(
      preset => preset.id !== presetId
    );

    this._savePresets();
    
    this._broadcastEvent(EVENTS.PRESETS_CHANGED, {
      professionalType: this._currentProfessional,
      presets: this.getCurrentPresets(),
    });
  }

  /**
   * Actualiza los presets por defecto de un profesional
   */
  public updateProfessionalDefaults(
    professionalType: string,
    defaultPresetIds: string[]
  ) {
    let professionalConfig = this._professionalPresets.get(professionalType);
    
    if (!professionalConfig) {
      professionalConfig = {
        professionalType,
        defaultPresets: defaultPresetIds,
      };
      this._professionalPresets.set(professionalType, professionalConfig);
    } else {
      professionalConfig.defaultPresets = defaultPresetIds;
    }

    this._savePresets();
    
    if (this._currentProfessional === professionalType) {
      this._broadcastEvent(EVENTS.PRESETS_CHANGED, {
        professionalType,
        presets: this.getCurrentPresets(),
      });
    }
  }

  /**
   * Guarda los presets en el almacenamiento local
   */
  private _savePresets() {
    try {
      const professionalPresetsData = Array.from(this._professionalPresets.entries()).map(
        ([key, value]) => ({
          professionalType: key,
          ...value,
        })
      );
      
      localStorage.setItem(
        'medit_layout_presets',
        JSON.stringify(professionalPresetsData)
      );
    } catch (error) {
      console.error('Error al guardar los presets de layout:', error);
    }
  }

  /**
   * Carga los presets desde el almacenamiento local
   */
  public loadPresets() {
    try {
      const savedData = localStorage.getItem('medit_layout_presets');
      if (savedData) {
        const professionalPresetsData = JSON.parse(savedData);
        
        professionalPresetsData.forEach((config: ProfessionalPresets) => {
          this._professionalPresets.set(config.professionalType, config);
        });
      }
    } catch (error) {
      console.error('Error al cargar los presets de layout:', error);
    }
  }

  /**
   * Obtiene el preset inicial para el profesional actual
   */
  public getInitialPreset(): LayoutPreset | null {
    const presets = this.getCurrentPresets();
    return presets.length > 0 ? presets[0] : null;
  }

  /**
   * Emite un evento cuando se selecciona un preset
   */
  public selectPreset(preset: LayoutPreset) {
    this._broadcastEvent(EVENTS.PRESET_SELECTED, {
      preset,
      professionalType: this._currentProfessional,
    });
  }
}

export default LayoutPresetsService;